import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Plus, Edit2, Trash2, Download, RefreshCw, ReceiptText, Paperclip } from 'lucide-react';
import { PermissionGuard, usePermissions } from '@components/PermissionGuard';
import { companyService } from '@services/companyService';
import { serviceInvoiceService } from '@services/serviceInvoiceService';
import type { Company } from '@/types/company';
import type { ServiceInvoice, ServiceInvoiceListResult } from '@/types/serviceInvoice';
import { formatCurrencyBRL, formatDateTimeBR } from '../financial/financialUtils';
import {
  LoadingSpinner,
  ErrorBox,
  SuccessBox,
  PermissionFallback,
  SummaryCard,
  ModalShell,
  selectCls,
  inputCls,
  labelCls,
  primaryBtnCls,
  secondaryBtnCls,
} from '../financial/financialShared';

const MONTHS = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB
const ACCEPTED_EXTENSIONS = '.pdf,.xml,.png,.jpg,.jpeg';

const getYearOptions = (): number[] => {
  const currentYear = new Date().getFullYear();
  const years: number[] = [];
  for (let y = currentYear - 5; y <= currentYear + 1; y++) years.push(y);
  return years;
};

/** Data/hora atual no formato aceito pelo input datetime-local (yyyy-MM-ddTHH:mm). */
const nowLocalInputValue = (): string => {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
};

/** Converte "yyyy-MM-dd HH:mm" (API) em "yyyy-MM-ddTHH:mm" (input datetime-local). */
const toDatetimeLocal = (issueTimestamp: string): string =>
  issueTimestamp.replace(' ', 'T').slice(0, 16);

// ─── Modal de nota (nova / editar) ──────────────────────────────

interface ServiceInvoiceModalProps {
  invoice: ServiceInvoice | null; // null = nova
  companies: Company[];
  defaultCompanyId: number | null;
  onSave: (payload: FormData) => Promise<void>;
  onClose: () => void;
}

const ServiceInvoiceModal: React.FC<ServiceInvoiceModalProps> = ({
  invoice,
  companies,
  defaultCompanyId,
  onSave,
  onClose,
}) => {
  const isEdit = invoice != null;

  const [number, setNumber] = useState(invoice?.number ?? '');
  const [companyId, setCompanyId] = useState<number | null>(
    invoice?.companyId ?? defaultCompanyId ?? (companies.length > 0 ? companies[0].company_id : null),
  );
  const [totalAmount, setTotalAmount] = useState(invoice != null ? String(invoice.totalAmount) : '');
  const [issueTimestamp, setIssueTimestamp] = useState(
    invoice != null ? toDatetimeLocal(invoice.issueTimestamp) : nowLocalInputValue(),
  );
  const [description, setDescription] = useState(invoice?.description ?? '');
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    const selected = e.target.files?.[0] ?? null;
    if (selected && selected.size > MAX_FILE_SIZE_BYTES) {
      setFile(null);
      e.target.value = '';
      setError('O arquivo selecionado excede o tamanho máximo de 10 MB');
      return;
    }
    setFile(selected);
  };

  const handleSubmit = async () => {
    setError(null);
    const parsedAmount = parseFloat(totalAmount.replace(',', '.'));
    if (!number.trim()) {
      setError('Informe o número da nota');
      return;
    }
    if (companyId == null) {
      setError('Selecione a empresa');
      return;
    }
    if (isNaN(parsedAmount) || parsedAmount <= 0) {
      setError('Informe um valor positivo');
      return;
    }
    if (!issueTimestamp) {
      setError('Informe a data/hora de emissão');
      return;
    }
    if (file && file.size > MAX_FILE_SIZE_BYTES) {
      setError('O arquivo selecionado excede o tamanho máximo de 10 MB');
      return;
    }

    const fd = new FormData();
    fd.append('number', number.trim());
    fd.append('companyId', String(companyId));
    fd.append('totalAmount', parsedAmount.toFixed(2)); // decimal com ponto, ex "1500.00"
    fd.append('issueTimestamp', issueTimestamp); // "yyyy-MM-ddTHH:mm" (datetime-local)
    if (description.trim()) fd.append('description', description.trim());
    if (file) fd.append('file', file);

    try {
      setSaving(true);
      await onSave(fd);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar nota fiscal de serviço');
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell
      title={isEdit ? 'Editar Nota de Serviço' : 'Nova Nota de Serviço'}
      icon={<ReceiptText size={20} className="text-emerald-600" />}
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose} disabled={saving} className={secondaryBtnCls}>
            Cancelar
          </button>
          <button type="button" onClick={handleSubmit} disabled={saving} className={primaryBtnCls}>
            {saving ? <RefreshCw size={14} className="animate-spin" /> : null}
            {saving ? 'Salvando...' : 'Salvar'}
          </button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Número *</label>
          <input
            className={inputCls}
            value={number}
            onChange={(e) => setNumber(e.target.value)}
            maxLength={20}
            autoFocus
          />
        </div>
        <div>
          <label className={labelCls}>Empresa *</label>
          <select
            className={selectCls}
            value={companyId ?? ''}
            onChange={(e) => setCompanyId(e.target.value ? Number(e.target.value) : null)}
          >
            {companyId == null && <option value="">— Selecione —</option>}
            {companies.map((c) => (
              <option key={c.company_id} value={c.company_id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Valor (R$) *</label>
          <input
            className={inputCls}
            type="number"
            min="0.01"
            step="0.01"
            value={totalAmount}
            onChange={(e) => setTotalAmount(e.target.value)}
            placeholder="0,00"
          />
        </div>
        <div>
          <label className={labelCls}>Data/Hora de emissão *</label>
          <input
            className={inputCls}
            type="datetime-local"
            value={issueTimestamp}
            onChange={(e) => setIssueTimestamp(e.target.value)}
          />
        </div>
      </div>
      <div>
        <label className={labelCls}>Descrição</label>
        <textarea
          className={inputCls}
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Descrição do serviço (opcional)"
        />
      </div>
      <div>
        <label className={labelCls}>Arquivo (PDF, XML, PNG, JPG — máx. 10 MB)</label>
        <input
          className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-lg file:border-0 file:bg-emerald-50 file:px-3 file:py-2 file:text-sm file:font-medium file:text-emerald-700 hover:file:bg-emerald-100"
          type="file"
          accept={ACCEPTED_EXTENSIONS}
          onChange={handleFileChange}
        />
        {file ? (
          <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
            <Paperclip size={12} /> Selecionado: {file.name}
          </p>
        ) : (
          isEdit &&
          invoice.hasFile &&
          invoice.fileName && (
            <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
              <Paperclip size={12} /> Arquivo atual: {invoice.fileName} (selecione um novo para substituir)
            </p>
          )
        )}
      </div>
      {error && <ErrorBox message={error} />}
    </ModalShell>
  );
};

// ─── View Principal ─────────────────────────────────────────────

export const ServiceInvoiceListView: React.FC = () => {
  const { checkPermission } = usePermissions();
  const canWrite = checkPermission('service_invoices.write').hasPermission;

  const now = new Date();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [companyId, setCompanyId] = useState<number | null>(null); // null = Todas
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState<number | null>(now.getMonth() + 1); // null = Todos

  const [result, setResult] = useState<ServiceInvoiceListResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [modal, setModal] = useState<{ invoice: ServiceInvoice | null } | null>(null);
  const [busy, setBusy] = useState(false);

  // Empresas (domínio compartilhado)
  useEffect(() => {
    companyService
      .getCompanies()
      .then(setCompanies)
      .catch((err) => setError(err instanceof Error ? err.message : 'Erro ao carregar empresas'));
  }, []);

  const companyNameById = useMemo(() => {
    const map = new Map<number, string>();
    for (const c of companies) map.set(c.company_id, c.name);
    return map;
  }, [companies]);

  const loadInvoices = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await serviceInvoiceService.list(year, month ?? undefined, companyId ?? undefined);
      setResult(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar notas fiscais de serviço');
    } finally {
      setLoading(false);
    }
  }, [year, month, companyId]);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  // ── Ações ───────────────────────────────────────────────────

  const handleSave = async (payload: FormData) => {
    if (modal?.invoice) {
      await serviceInvoiceService.update(modal.invoice.serviceInvoiceId, payload);
      setNotice('Nota fiscal de serviço atualizada com sucesso');
    } else {
      await serviceInvoiceService.create(payload);
      setNotice('Nota fiscal de serviço criada com sucesso');
    }
    await loadInvoices();
  };

  const handleDelete = async (invoice: ServiceInvoice) => {
    if (!window.confirm(`Excluir a nota fiscal de serviço nº ${invoice.number}?`)) return;
    setBusy(true);
    setError(null);
    try {
      await serviceInvoiceService.remove(invoice.serviceInvoiceId);
      setNotice('Nota fiscal de serviço excluída com sucesso');
      await loadInvoices();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao excluir nota fiscal de serviço');
    } finally {
      setBusy(false);
    }
  };

  const handleDownload = async (invoice: ServiceInvoice) => {
    setBusy(true);
    setError(null);
    try {
      await serviceInvoiceService.downloadFile(invoice.serviceInvoiceId, invoice.fileName);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao baixar arquivo');
    } finally {
      setBusy(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────

  const invoices = result?.invoices ?? [];

  return (
    <PermissionGuard permission="service_invoices.read" fallback={<PermissionFallback label="as notas fiscais de serviço" />}>
      <div className="space-y-6">
        {/* Modal */}
        {modal && (
          <ServiceInvoiceModal
            invoice={modal.invoice}
            companies={companies}
            defaultCompanyId={companyId}
            onSave={handleSave}
            onClose={() => setModal(null)}
          />
        )}

        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Notas Fiscais de Serviço</h1>
            <p className="text-sm text-slate-500 mt-1">Registro das notas fiscais de serviço emitidas</p>
          </div>
          {canWrite && (
            <div className="flex flex-wrap gap-2">
              <button className={primaryBtnCls} onClick={() => setModal({ invoice: null })}>
                <Plus size={16} /> Nova Nota de Serviço
              </button>
            </div>
          )}
        </div>

        {/* Filtros */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className={labelCls}>Ano</label>
              <select className={selectCls + ' w-28'} value={year} onChange={(e) => setYear(Number(e.target.value))}>
                {getYearOptions().map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Mês</label>
              <select
                className={selectCls + ' w-36'}
                value={month ?? ''}
                onChange={(e) => setMonth(e.target.value ? Number(e.target.value) : null)}
              >
                <option value="">Todos</option>
                {MONTHS.map((m, idx) => (
                  <option key={m} value={idx + 1}>
                    {m}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Empresa</label>
              <select
                className={selectCls + ' w-64'}
                value={companyId ?? ''}
                onChange={(e) => setCompanyId(e.target.value ? Number(e.target.value) : null)}
              >
                <option value="">Todas</option>
                {companies.map((c) => (
                  <option key={c.company_id} value={c.company_id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {notice && <SuccessBox message={notice} onDismiss={() => setNotice(null)} />}
        {error && <ErrorBox message={error} onRetry={loadInvoices} />}
        {loading && <LoadingSpinner />}

        {!loading && result && (
          <>
            {/* Cards de resumo */}
            <div className="grid grid-cols-2 gap-4 sm:max-w-md">
              <SummaryCard
                label="Total do período"
                value={formatCurrencyBRL(result.total)}
                valueClassName="text-emerald-600"
              />
              <SummaryCard label="Notas no período" value={String(invoices.length)} />
            </div>

            {/* Tabela */}
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="text-left py-2 px-3 font-semibold text-slate-600">Número</th>
                      <th className="text-left py-2 px-3 font-semibold text-slate-600">Empresa</th>
                      <th className="text-left py-2 px-3 font-semibold text-slate-600">Data de Emissão</th>
                      <th className="text-right py-2 px-3 font-semibold text-slate-600">Valor</th>
                      <th className="text-left py-2 px-3 font-semibold text-slate-600">Descrição</th>
                      <th className="text-center py-2 px-3 font-semibold text-slate-600 w-20">Arquivo</th>
                      {canWrite && <th className="text-right py-2 px-3 font-semibold text-slate-600 w-24">Ações</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.length === 0 && (
                      <tr>
                        <td colSpan={canWrite ? 7 : 6} className="py-10 text-center text-slate-400">
                          Nenhuma nota fiscal de serviço no período
                        </td>
                      </tr>
                    )}
                    {invoices.map((invoice) => (
                      <tr key={invoice.serviceInvoiceId} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-2 px-3 text-slate-700 font-medium whitespace-nowrap">{invoice.number}</td>
                        <td className="py-2 px-3 text-slate-700">
                          {companyNameById.get(invoice.companyId) ?? `Empresa ${invoice.companyId}`}
                        </td>
                        <td className="py-2 px-3 text-slate-700 whitespace-nowrap">
                          {formatDateTimeBR(invoice.issueTimestamp)}
                        </td>
                        <td className="py-2 px-3 text-right font-medium text-emerald-600 whitespace-nowrap">
                          {formatCurrencyBRL(invoice.totalAmount)}
                        </td>
                        <td className="py-2 px-3 text-slate-500">{invoice.description || '—'}</td>
                        <td className="py-2 px-3 text-center">
                          {invoice.hasFile ? (
                            <button
                              className="p-1.5 text-slate-400 hover:text-emerald-600 rounded"
                              title={invoice.fileName ? `Baixar ${invoice.fileName}` : 'Baixar arquivo'}
                              onClick={() => handleDownload(invoice)}
                              disabled={busy}
                            >
                              <Download size={15} />
                            </button>
                          ) : (
                            <span className="text-slate-300">—</span>
                          )}
                        </td>
                        {canWrite && (
                          <td className="py-2 px-3">
                            <div className="flex items-center justify-end gap-1">
                              <button
                                className="p-1.5 text-slate-400 hover:text-emerald-600 rounded"
                                title="Editar"
                                onClick={() => setModal({ invoice })}
                              >
                                <Edit2 size={15} />
                              </button>
                              <button
                                className="p-1.5 text-slate-400 hover:text-red-500 rounded"
                                title="Excluir"
                                onClick={() => handleDelete(invoice)}
                                disabled={busy}
                              >
                                <Trash2 size={15} />
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </PermissionGuard>
  );
};

export default ServiceInvoiceListView;
